import { queryOptions } from '@tanstack/react-query'

import { fetchJson } from '@/api/client'
import { keys } from '@/api/keys'
import type { CostLedgerRow } from '@/schema'

/**
 * `/api/costs/*` (§5.9). Every figure is the CLI's list-price ESTIMATE, never
 * a bill (§4.8 `cost_basis`); the UI says so wherever it prints a dollar.
 */

export type CostGroupBy = 'day' | 'kind' | 'model' | 'purpose'

export interface CostSummaryRow {
  key: string
  n: number
  usd: number
  errors: number
  avg_ms: number | null
}

/** Per-schema-hash first-call vs later-call API duration: grammar-compile cost made visible. */
export interface SchemaHashDuration {
  schema_hash: string
  first_ms: number | null
  later_avg_ms: number | null
  n: number
  [k: string]: unknown
}

export interface CostSummary {
  rows: CostSummaryRow[]
  billing: 'api' | 'subscription' | 'mock'
  today_usd: number
  daily_budget_usd: number
  daily_budget_default_reason?: string
  schema_hashes?: SchemaHashDuration[]
  note?: string
}

export interface CostSummaryParams {
  from?: string
  to?: string
  group_by?: CostGroupBy
}

export interface LedgerParams {
  job_id?: number
  story_uid?: string
  asset_uid?: string
  limit?: number
}

export function getCostSummary(params: CostSummaryParams = {}, signal?: AbortSignal): Promise<CostSummary> {
  return fetchJson<CostSummary>('/api/costs/summary', { query: { group_by: 'day', ...params }, signal })
}

export function getLedger(params: LedgerParams = {}, signal?: AbortSignal): Promise<{ items: CostLedgerRow[] }> {
  return fetchJson('/api/costs/ledger', { query: { limit: 100, ...params }, signal })
}

export const costSummaryQuery = (params: CostSummaryParams = {}) =>
  queryOptions({
    queryKey: keys.costs.summary(params),
    queryFn: ({ signal }) => getCostSummary(params, signal),
    staleTime: 30_000,
    retry: 1,
  })

export const ledgerQuery = (params: LedgerParams = {}) =>
  queryOptions({
    queryKey: keys.costs.ledger(params),
    queryFn: ({ signal }) => getLedger(params, signal),
    staleTime: 30_000,
    retry: 1,
  })
